import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { v4 as uuidv4 } from 'uuid';

function BotChat() {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [bots, setBots] = useState([]);
    const [selectedBot, setSelectedBot] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingBots, setLoadingBots] = useState(true);
    const [sessionId, setSessionId] = useState(() => uuidv4());
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const getToken = useCallback(async () => {
        try {
            const result = await instance.acquireTokenSilent({ ...loginRequest, account });
            return result.accessToken;
        } catch {
            const result = await instance.acquireTokenRedirect(loginRequest);
            return result?.accessToken;
        }
    }, [instance, account]);

    const fetchBots = useCallback(async () => {
        try {
            setLoadingBots(true);
            const token = await getToken();
            const response = await fetch('/api/bots', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch bots');
            const data = await response.json();
            const sorted = [...data].sort((a, b) => a.botName.localeCompare(b.botName));
            setBots(sorted);
            if (sorted.length > 0) setSelectedBot(sorted[0]);
        } catch (error) {
            console.error('Error fetching bots:', error);
        } finally {
            setLoadingBots(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchBots();
    }, [fetchBots]);

    const handleBotChange = (botType) => {
        const bot = bots.find(b => b.botType === botType);
        setSelectedBot(bot);
        setMessages([]);
        setSessionId(uuidv4());
        setInput('');
    };

    const handleNewChat = () => {
        setMessages([]);
        setSessionId(uuidv4());
        setInput('');
    };

    const sendMessage = useCallback(async (text) => {
        if (!text.trim() || loading || !selectedBot) return;

        const userMessage = {
            id: uuidv4(),
            role: 'user',
            content: text.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        try {
            const token = await getToken();

            const userId = account?.idTokenClaims?.oid
                ?? account?.idTokenClaims?.sub
                ?? account?.username
                ?? null;

            const userName = account?.idTokenClaims?.preferred_username
                ?? account?.idTokenClaims?.upn
                ?? account?.username
                ?? null;

            const response = await fetch('/api/bots/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    botType: selectedBot.botType,
                    message: text.trim(),
                    sessionId,
                    userId,
                    userName
                })
            });

            if (!response.ok) throw new Error(`Request failed: ${response.status}`);

            const data = await response.json();

            setMessages(prev => [...prev, {
                id: uuidv4(),
                role: 'assistant',
                content: data.answer,
                timestamp: new Date(),
                responseTimeMs: data.responseTimeMs,
                tokensUsed: data.tokensUsed
            }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                id: uuidv4(),
                role: 'assistant',
                content: 'Something went wrong. Please try again.',
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setLoading(false);
        }
    }, [loading, selectedBot, sessionId, getToken, account]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
    };

    const userInitials = account?.username?.charAt(0).toUpperCase() || 'U';
    const getInitials = (role) => role === 'user' ? userInitials : '🤖';

    if (loadingBots) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6c757d' }}>
                Loading available bots...
            </div>
        );
    }

    if (bots.length === 0) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤖</div>
                <h2 style={{ color: '#1a4d2e', marginBottom: '0.5rem' }}>No Bots Available</h2>
                <p style={{ color: '#6c757d' }}>No knowledge bots are currently assigned to this application. Contact your administrator.</p>
            </div>
        );
    }

    return (
        <div className="chat-layout">
            <div className="chat-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <div>
                        <div className="chat-header-title">Knowledge Bots</div>
                        <div className="chat-header-subtitle">RAG-powered knowledge assistant</div>
                    </div>
                    <select
                        value={selectedBot?.botType || ''}
                        onChange={(e) => handleBotChange(e.target.value)}
                        style={{
                            padding: '0.4rem 0.75rem',
                            border: '1.5px solid #dee2e6',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            color: '#1a4d2e',
                            fontWeight: '500',
                            background: 'white',
                            cursor: 'pointer'
                        }}
                    >
                        {bots.map(b => (
                            <option key={b.botId} value={b.botType}>{b.botName}</option>
                        ))}
                    </select>
                </div>
                <button className="btn-new-chat" onClick={handleNewChat}>+ New Chat</button>
            </div>

            {selectedBot && (
                <div style={{
                    padding: '0.6rem 1.5rem',
                    background: '#f0f4f8',
                    borderBottom: '1px solid #e0e4e8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.8rem',
                    color: '#1a4d2e'
                }}>
                    <span style={{ fontWeight: 600 }}>Active:</span>
                    <span>{selectedBot.botName}</span>
                    <span style={{ color: '#adb5bd' }}>·</span>
                    <span style={{ color: '#6c757d' }}>Powered by {selectedBot.primaryProviderName}</span>
                    <span style={{ color: '#adb5bd' }}>·</span>
                    <span style={{ color: '#6c757d' }}>Answers from approved company knowledge only</span>
                </div>
            )}

            <div className="chat-messages">
                {messages.length === 0 && !loading && (
                    <div className="welcome-screen">
                        <div className="welcome-icon">🤖</div>
                        <div className="welcome-title">{selectedBot?.botName}</div>
                        <div className="welcome-subtitle">
                            Ask me anything within my knowledge base. I answer only from approved company content.
                        </div>
                    </div>
                )}

                {messages.map(msg => (
                    <div key={msg.id} className={`message ${msg.role}`}>
                        <div className="message-avatar">{getInitials(msg.role)}</div>
                        <div>
                            <div className={`message-bubble ${msg.isError ? 'error' : ''}`}>
                                {msg.role === 'assistant' ? (
                                    <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                                ) : (
                                    msg.content
                                )}
                            </div>
                            <div className="message-meta">
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {msg.responseTimeMs && ` · ${(msg.responseTimeMs / 1000).toFixed(1)}s`}
                                {msg.tokensUsed && ` · ${msg.tokensUsed} tokens`}
                            </div>
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="message assistant">
                        <div className="message-avatar">🤖</div>
                        <div className="message-bubble">
                            <div className="typing-indicator">
                                <div className="typing-dot" />
                                <div className="typing-dot" />
                                <div className="typing-dot" />
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
                <div className="chat-input-box">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        placeholder={`Ask ${selectedBot?.botName || 'the bot'} a question...`}
                        rows={1}
                        disabled={loading}
                    />
                    <button
                        className="btn-send"
                        onClick={() => sendMessage(input)}
                        disabled={!input.trim() || loading}
                    >
                        ↑
                    </button>
                </div>
                <div className="chat-input-hint">
                    Answers are grounded in approved company knowledge · Hale Partnership Enterprise AI
                </div>
            </div>
        </div>
    );
}

export default BotChat;